import { Control, useWatch } from 'react-hook-form'
import { ProjectFormValues } from 'types/project.type'
import { PropertyFormValues } from 'types/property.type'
import { isValidAndNonEmpty } from 'utils'
import { isValidEmail, isValidPhoneNumber } from 'utils/string-formatters'

export const useProjectInformationNextButtonDisabled = (control: Control<any>, errors): boolean => {
  const formValues = useWatch({ control })

  return (
    !formValues?.projectType ||
    !formValues?.clientStartDate ||
    !formValues?.clientDueDate ||
    !isValidAndNonEmpty(formValues?.sowOriginalContractAmount) ||
    !formValues?.documents ||
    !!errors.documents
  )
}

export const fieldsLimitNumber = 255

export const usePropertyInformationNextDisabled = (control: Control<any>, isDuplicateAddress: boolean): boolean => {
  const formValues = useWatch({ control })
  const lengthLimit =
    formValues.streetAddress?.length > fieldsLimitNumber ||
    formValues.unitNo?.length > fieldsLimitNumber ||
    formValues.propertyNotes?.length > 65535 ||
    formValues.city?.length > fieldsLimitNumber
  // Acknowledge check appears based on address selected from saved address list so here we also check that in case user has entered new address
  const isAcknowledgeCheck = formValues?.property && isDuplicateAddress ? formValues?.acknowledgeCheck : true

  return (
    !formValues.streetAddress ||
    !formValues.city ||
    !formValues.state?.value ||
    !formValues.zipCode ||
    !formValues.market?.value ||
    !formValues.client?.value ||
    !isAcknowledgeCheck ||
    lengthLimit
  )
}

export const useAddressShouldBeVerified = (control: Control<ProjectFormValues | PropertyFormValues>): boolean => {
  const property = useWatch({ name: 'property', control })

  return !property
}

export const usePropertyResidentSaveButtonDisabled = (control: Control<any>): boolean => {
  const formValues = useWatch({ control })
  return formValues?.residents?.some(
    resident =>
      !isValidPhoneNumber(resident.phoneNumber) ||
      !isValidEmail(resident.emailAddress) ||
      !isValidAndNonEmpty(resident.phoneNumber) ||
      !isValidAndNonEmpty(resident.firstName) ||
      !isValidAndNonEmpty(resident.lastName) ||
      resident.firstName.length > fieldsLimitNumber ||
      resident.lastName.length > fieldsLimitNumber ||
      resident.emailAddress.length > fieldsLimitNumber,
  )
}
export const useProjectManagementSaveButtonDisabled = (control: Control<ProjectFormValues>): boolean => {
  const formValues = useWatch({ control })
  const isSuperPhoneNumber = isValidPhoneNumber(formValues?.superPhoneNumber)
  return (
    !formValues?.projectManager?.value ||
    !formValues?.projectCoordinator?.value ||
    !formValues?.client?.value ||
    !isSuperPhoneNumber
  )
}

export const useWOStartDateMin = (control: Control<ProjectFormValues | PropertyFormValues>): string => {
  const clientStartDate = useWatch({ name: 'clientStartDate', control })

  return clientStartDate ? new Date(clientStartDate).toISOString().split('T')[0] : ''
}

export const useNewClientNextButtonDisabled = ({ control }: any) => {
  const [companyName, paymentTerm, streetAddress, city, state] = useWatch({
    control,
    name: ['companyName', 'paymentTerm', 'streetAddress', 'city', 'state'],
  })
  const contact = useWatch({ control, name: ['contacts'] })
  const contactArray = contact?.length > 0 ? contact[0] : []

  const accountPayable = useWatch({ control, name: ['accountPayableContactInfos'] })
  const accountPayableArray = accountPayable?.length > 0 ? accountPayable[0] : []

  return {
    isNewClientDetails: !companyName || !paymentTerm || !streetAddress || !city || !state,
    isContactSection: contactArray?.some(
      contact => !contact.contact || !contact.emailAddress || !contact.phoneNumber || !contact.market,
    ),
    isAccountPayableSection: accountPayableArray?.some(
      accountPayable =>
        !accountPayable.contact ||
        !accountPayable.phoneNumber ||
        !accountPayable.emailAddress ||
        !accountPayable.comments,
    ),
  }
}
